import React, { useCallback, useMemo, useRef, useState } from 'react'
import classnames from 'classnames/bind'
import {
  SearchField,
  Chip,
  ChipColor,
  Text,
  MorpheusApplicationContext,
} from '@adc/ui-morpheus'
import { search, primitives } from '@adc/blueprint'
import styles from './locationSearchInput.styles.scss'

const cx = classnames.bind(styles)

/**
 * A parsed location entry from the serialized locations string.
 * Format in parameters.locations: comma-separated geoid URNs or "{type}:{value}" pairs.
 */
interface ParsedLocation {
  /** Display label for the chip */
  label: string
  /** Raw serialized value stored in the locations string */
  raw: string
}

export interface LocationSearchInputProps {
  /** Serialized locations string from parameters.locations */
  value: string
  /** Called with the updated serialized locations string */
  onChange: (locationsString: string) => void
  /** Resolved labels for geoid entries (geoid:id → display label) */
  labelMap?: Record<string, string>
  max?: number
  disabled?: boolean
  elmId?: string
  label?: string
}

/**
 * Parse the serialized locations string into individual entries.
 * The string is comma-separated: "geoid:123,geoid:456" or "city:Austin TX,zip:78701"
 * Aligned with graph-api parseSerializedGeolocationValue grammar.
 */
function parseLocations(locationsStr: string): ParsedLocation[] {
  if (!locationsStr) return []
  return locationsStr
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(raw => ({
      raw,
      label: formatLocationLabel(raw),
    }))
}

/**
 * Serialize parsed locations back to a pipe-separated string.
 */
function serializeLocations(locations: ParsedLocation[]): string {
  return locations.map(l => l.raw).join(',')
}

/**
 * Format a raw location entry for display.
 * If it's a geoid URN, show it as-is (the structured_locations resolver provides labels).
 * If it's a "type:value" format, show the value portion.
 */
function formatLocationLabel(raw: string): string {
  if (raw.startsWith('geoid:')) {
    // For geoid entries, display the id (actual label comes from structured_locations)
    return raw.slice('geoid:'.length)
  }
  // Legacy format: "city:Austin, TX" -> "Austin, TX"
  const colonIdx = raw.indexOf(':')
  if (colonIdx > 0) {
    return raw.slice(colonIdx + 1)
  }
  return raw
}

const LocationSearchInput: React.FC<LocationSearchInputProps> = ({
  value,
  onChange,
  labelMap = {},
  max = 5,
  disabled = false,
  elmId = 'location-search',
  label,
}) => {
  const { graphWebsocket } = MorpheusApplicationContext.useApplicationContext()

  const includedSuggestionTypes = useMemo(
    () =>
      ['state', 'county', 'city', 'zip'] as (
        | 'state'
        | 'county'
        | 'city'
        | 'zip'
      )[],
    []
  )

  const deviceStorageRef = useRef(
    new primitives.DeviceStorageAdapter({
      storage: new primitives.NullDeviceStorage({ namespace: 'locationPref' }),
    })
  )

  const { data, actions } = search.hooks.useLocationSuggestions({
    includedSuggestionTypes,
    filters: null,
    currentLocation: null,
    deviceStorage: deviceStorageRef.current,
    elmId,
    graphWebsocket,
  })

  // Local labels for newly-added suggestions (not yet in server-resolved labelMap)
  const [localLabels, setLocalLabels] = useState<Record<string, string>>({})

  const mergedLabelMap = useMemo(
    () => ({ ...labelMap, ...localLabels }),
    [labelMap, localLabels]
  )

  const parsedLocations = useMemo(
    () =>
      parseLocations(value).map(loc => ({
        ...loc,
        label: mergedLabelMap[loc.raw] ?? loc.label,
      })),
    [value, mergedLabelMap]
  )
  const isAtMax = parsedLocations.length >= max

  const onSearchChange = useCallback(
    (_: unknown, searchInfo: { value?: string }) => {
      if (!isAtMax) {
        actions.update({ searchText: searchInfo.value ?? '' })
      }
    },
    [actions, isAtMax]
  )

  const onResultSelect = useCallback(
    (_: unknown, resultData: { result: { index: number } }) => {
      const suggestion = data.suggestions?.[resultData.result.index]
      if (!suggestion) return

      // Build the serialized location value
      // Prefer geoid URN (relates_to_urn) if available, otherwise use type:label format
      let rawValue: string
      const suggestionGeoid = suggestion.relates_to_urn?.startsWith(
        'adc:geoid:'
      )
        ? suggestion.relates_to_urn.slice('adc:geoid:'.length)
        : null

      if (suggestionGeoid) {
        rawValue = `geoid:${suggestionGeoid}`
      } else {
        const locationType = suggestion.filters?.property_zip
          ? 'zip'
          : suggestion.filters?.property_city
          ? 'city'
          : suggestion.filters?.property_county
          ? 'county'
          : suggestion.filters?.property_state
          ? 'state'
          : 'city'
        rawValue = `${locationType}:${suggestion.label}`
      }

      // Deduplicate by raw value, geoid, or cross-format state match
      const isDuplicate = parsedLocations.some(l => {
        if (l.raw === rawValue) return true
        if (suggestionGeoid && l.raw === `geoid:${suggestionGeoid}`) return true
        // Cross-format: existing state:XX matches new geoid for same state
        if (suggestionGeoid && l.raw.startsWith('state:')) {
          const existingCode = l.raw.slice('state:'.length).toUpperCase()
          const newCode = suggestion.filters?.property_state?.toUpperCase()
          if (existingCode && existingCode === newCode) return true
        }
        // Cross-format: existing geoid matches new state:XX (no geoid available)
        if (!suggestionGeoid && suggestion.filters?.property_state) {
          const newCode = suggestion.filters.property_state.toUpperCase()
          // Check if existing geoid entry's label matches this state
          if (l.raw.startsWith('geoid:')) {
            const existingLabel = mergedLabelMap[l.raw]?.toUpperCase()
            if (existingLabel === newCode) return true
          }
        }
        return false
      })
      if (!isDuplicate) {
        // Track the label locally so the chip shows it immediately
        setLocalLabels(prev => ({ ...prev, [rawValue]: suggestion.label }))
        const updated = [
          ...parsedLocations,
          { raw: rawValue, label: suggestion.label },
        ]
        onChange(serializeLocations(updated))
      }

      actions.update({ searchText: '' })
    },
    [data.suggestions, parsedLocations, onChange, actions]
  )

  const removeLocation = useCallback(
    (index: number) => {
      const updated = parsedLocations.filter((_, i) => i !== index)
      onChange(serializeLocations(updated))
    },
    [parsedLocations, onChange]
  )

  const results = useMemo(() => {
    if (!data.suggestions) return []
    return data.suggestions.map((suggestion, index) => ({
      key: `${index}_${suggestion.type}`,
      title: suggestion.label,
      type: suggestion.type,
      searchText: data.searchText,
      index,
    }))
  }, [data.suggestions, data.searchText])

  const resultRenderer = useCallback(
    ({
      title,
      type,
      searchText,
    }: {
      title: string
      type: string
      searchText: string
    }) => {
      const searchLower = (searchText ?? '').toLowerCase()
      const idx = title.toLowerCase().indexOf(searchLower)
      let labelContent: React.ReactNode = title
      if (idx >= 0 && searchLower.length > 0) {
        const before = title.slice(0, idx)
        const match = title.slice(idx, idx + searchLower.length)
        const after = title.slice(idx + searchLower.length)
        labelContent = (
          <>
            {before && <span>{before}</span>}
            <span className={cx('highlight')}>{match}</span>
            {after && <span>{after}</span>}
          </>
        )
      }
      return (
        <div className={cx('suggestion-row')}>
          <span className={cx('suggestion-label')}>{labelContent}</span>
          <span className={cx('suggestion-type')}>{type}</span>
        </div>
      )
    },
    []
  )

  return (
    <div className={cx('location-search')}>
      {label && (
        <Text size='tiny' color='gray'>
          {label}
        </Text>
      )}
      <div className={cx('search-input', { disabled: isAtMax || disabled })}>
        <SearchField
          elmId={`${elmId}_input`}
          placeholder={
            isAtMax ? `Maximum ${max} locations` : 'County, City, ZIP'
          }
          loading={data.loading}
          minCharacters={1}
          results={results}
          resultRenderer={resultRenderer}
          onResultSelect={onResultSelect}
          onSearchChange={onSearchChange}
          value={data.searchText ?? ''}
          hideWrapper
          compact
          stickyOpen={false}
          disabled={isAtMax || disabled}
          selectFirstResult
        />
      </div>
      {parsedLocations.length > 0 && (
        <div className={cx('location-chips')}>
          {parsedLocations.map((loc, i) => (
            <Chip
              key={loc.raw}
              elmId={`${elmId}-chip-${i}`}
              content={loc.label}
              type='input'
              size='small'
              color={ChipColor.Info}
              selectionStyle='light'
              onClick={() => removeLocation(i)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default LocationSearchInput
